import { c as _c } from "react/compiler-runtime";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useState, useSyncExternalStore, type ComponentProps } from "react";
import { cn } from "@repo/blacksmith-ui/cn";
import { useKnownImageSize } from "./sessionInlineImages";

/**
 * A hung image request must not strand the session behind the loading
 * screen; the browser's own timeout is far longer than a user will wait.
 */
const MAX_HOLD_MS = 10_000;
const noop = () => undefined;
export type TranscriptHoldKind = "chunk" | "image" | "highlight" | "other";
const HOLD_KINDS: readonly TranscriptHoldKind[] = ["chunk", "image", "highlight", "other"];
export interface TranscriptLoadGateStats {
  /** `ms` is the union of that kind's hold intervals, not their sum. */
  holds: Record<TranscriptHoldKind, {
    count: number;
    ms: number;
  }>;
  timedOut: boolean;
  lastReleaseKind: TranscriptHoldKind | null;
}
interface KindTally {
  count: number;
  active: number;
  since: number;
  ms: number;
}

/**
 * Holds a session behind its loading screen until the first-rendered
 * transcript has fully landed: lazily loaded chunks resolved and every image
 * without a reserved box loaded (or failed). Armed once the transcript
 * commits, so holds taken during that commit are counted; settles
 * permanently the first time nothing holds it, after which later images
 * (streamed turns) are ignored.
 */
export class TranscriptLoadGate {
  private holds = 0;
  private reservedLoading = 0;
  private armed = false;
  private settled = false;
  private timedOut = false;
  private lastReleaseKind: TranscriptHoldKind | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly listeners = new Set<() => void>();
  private readonly tallies = new Map<TranscriptHoldKind, KindTally>(HOLD_KINDS.map(kind => [kind, {
    count: 0,
    active: 0,
    since: 0,
    ms: 0
  }]));
  hold(kind: TranscriptHoldKind = "other"): () => void {
    if (this.settled) return noop;
    const tally = this.tallies.get(kind)!;
    tally.count += 1;
    if (tally.active === 0) tally.since = performance.now();
    tally.active += 1;
    this.holds += 1;
    this.emit();
    let released = false;
    return () => {
      if (released) return;
      released = true;
      tally.active -= 1;
      if (tally.active === 0) tally.ms += performance.now() - tally.since;
      this.lastReleaseKind = kind;
      this.holds -= 1;
      this.emit();
    };
  }

  /**
   * An image with both width and height attributes has its exact box before
   * its bytes arrive, so it cannot move the transcript and never holds; it
   * only delays `imagesLoaded`.
   */
  holdImage(img: HTMLImageElement): () => void {
    if (this.settled || img.complete) return noop;
    const reserved = img.hasAttribute("width") && img.hasAttribute("height");
    const release = reserved ? this.trackReserved() : this.hold("image");
    const done = () => {
      img.removeEventListener("load", done);
      img.removeEventListener("error", done);
      release();
    };
    img.addEventListener("load", done);
    img.addEventListener("error", done);
    return done;
  }
  arm(): void {
    if (this.armed) return;
    this.armed = true;
    this.timer = setTimeout(() => {
      this.timedOut = !this.settled;
      this.settle();
    }, MAX_HOLD_MS);
    this.emit();
  }
  get isSettled(): boolean {
    return this.settled;
  }
  get isReady(): boolean {
    return this.settled || this.armed && this.holds === 0;
  }

  /** Ready, and every reserved image in the first-rendered transcript has loaded (or failed) too. */
  get imagesLoaded(): boolean {
    return this.isReady && this.reservedLoading === 0;
  }
  settle(): void {
    if (this.settled) return;
    this.settled = true;
    this.clearTimer();
    this.emit();
  }
  stats(): TranscriptLoadGateStats {
    const now = performance.now();
    const holds = {} as TranscriptLoadGateStats["holds"];
    for (const [kind, tally] of this.tallies) {
      const open = tally.active > 0 ? now - tally.since : 0;
      holds[kind] = {
        count: tally.count,
        ms: Math.round(tally.ms + open)
      };
    }
    return {
      holds,
      timedOut: this.timedOut,
      lastReleaseKind: this.lastReleaseKind
    };
  }
  dispose(): void {
    this.clearTimer();
  }
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private trackReserved(): () => void {
    this.reservedLoading += 1;
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      clearTimeout(timer);
      this.reservedLoading -= 1;
      this.emit();
    };
    const timer = setTimeout(release, MAX_HOLD_MS);
    return release;
  }
  private clearTimer(): void {
    if (this.timer === null) return;
    clearTimeout(this.timer);
    this.timer = null;
  }
  private emit(): void {
    for (const listener of this.listeners) listener();
  }
}
export const TranscriptLoadGateContext = createContext<TranscriptLoadGate | null>(null);

/**
 * One gate per thread. `active` is whether the transcript is rendered
 * (rather than the pane's own loading shell); the gate arms in a layout
 * effect, which runs after the transcript's refs and layout effects have
 * registered their holds. `skip` settles it outright for a transcript with
 * nothing to fetch (an optimistic first send, whose attachments are inline
 * data and whose handoff animation must not be covered).
 */
export function useTranscriptLoadGate(threadKey, t0) {
  const $ = _c(32);
  const {
    active,
    skip: t1
  } = t0;
  const skip = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== threadKey) {
    t2 = () => ({
      key: threadKey,
      gate: new TranscriptLoadGate()
    });
    $[0] = threadKey;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [current, setCurrent] = useState(t2);
  let gate;
  if ($[2] !== current.gate || $[3] !== current.key || $[4] !== threadKey) {
    gate = current.gate;
    if (current.key !== threadKey) {
      gate = new TranscriptLoadGate();
      setCurrent({
        key: threadKey,
        gate
      });
    }
    $[2] = current.gate;
    $[3] = current.key;
    $[4] = threadKey;
    $[5] = gate;
  } else {
    gate = $[5];
  }
  let t3;
  if ($[6] !== gate) {
    t3 = () => () => gate.dispose();
    $[6] = gate;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== gate) {
    t4 = [gate];
    $[8] = gate;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useEffect(t3, t4);
  let t5;
  if ($[10] !== gate.isReady) {
    t5 = () => gate.isReady;
    $[10] = gate.isReady;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const ready = useSyncExternalStore(gate.subscribe, t5, _temp);
  let t6;
  if ($[12] !== gate.imagesLoaded) {
    t6 = () => gate.imagesLoaded;
    $[12] = gate.imagesLoaded;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const imagesLoaded = useSyncExternalStore(gate.subscribe, t6, _temp2);
  let t7;
  if ($[14] !== active || $[15] !== gate || $[16] !== skip) {
    t7 = () => {
      if (skip) {
        gate.settle();
      } else {
        if (active) {
          gate.arm();
        }
      }
    };
    $[14] = active;
    $[15] = gate;
    $[16] = skip;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== active || $[19] !== gate || $[20] !== skip) {
    t8 = [active, gate, skip];
    $[18] = active;
    $[19] = gate;
    $[20] = skip;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  useLayoutEffect(t7, t8);
  let t9;
  if ($[22] !== gate || $[23] !== ready) {
    t9 = () => {
      if (ready) {
        gate.settle();
      }
    };
    $[22] = gate;
    $[23] = ready;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== gate || $[26] !== ready) {
    t10 = [gate, ready];
    $[25] = gate;
    $[26] = ready;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  useLayoutEffect(t9, t10);
  let t11;
  if ($[28] !== gate || $[29] !== imagesLoaded || $[30] !== ready) {
    t11 = {
      gate,
      ready,
      imagesLoaded
    };
    $[28] = gate;
    $[29] = imagesLoaded;
    $[30] = ready;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
}
function _temp2() {
  return true;
}
function _temp() {
  return true;
}
const subscribeNever = () => noop;

/** Whether the enclosing thread's first-rendered transcript has landed; true without a gate. */
export function useTranscriptLanded() {
  const $ = _c(2);
  const gate = useContext(TranscriptLoadGateContext);
  let t0;
  if ($[0] !== gate?.isReady) {
    t0 = () => gate?.isReady ?? true;
    $[0] = gate?.isReady;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useSyncExternalStore(gate?.subscribe ?? subscribeNever, t0, _temp3);
}

/**
 * `<img>` for transcript content: holds the gate until it loads unless its
 * box is reserved (see holdImage). Mounted while the gate is open, it loads
 * eagerly: a lazy image outside the viewport never fires `load`, and the
 * pinned-to-bottom transcript puts most of a long thread there. A reserved
 * image loads eagerly too, so the one on screen at reveal is not left empty
 * until the browser's lazy loading reaches it after the first paint.
 */
function _temp3() {
  return true;
}
export function TranscriptImage(props) {
  const $ = _c(8);
  const gate = useContext(TranscriptLoadGateContext);
  let t0;
  if ($[0] !== gate) {
    t0 = () => gate && !gate.isSettled ? "eager" : "lazy";
    $[0] = gate;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [loading] = useState(t0);
  let t1;
  if ($[2] !== gate) {
    t1 = img => {
      if (!img || !gate) {
        return;
      }
      return gate.holdImage(img);
    };
    $[2] = gate;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const ref = t1;
  let t2;
  if ($[4] !== loading || $[5] !== props || $[6] !== ref) {
    t2 = <img loading={loading} {...props} ref={ref} />;
    $[4] = loading;
    $[5] = props;
    $[6] = ref;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

/**
 * TranscriptImage for a thumbnail capped at `maxHeight` (and `maxWidth`) px.
 * A server-known size reserves the box the loaded image would take, e.g.
 * 1440x900 under a 192px cap → 307.2px wide at 1440 / 900, so it skips the gate.
 * The explicit (non-`auto`) aspect-ratio keeps the box from following the
 * natural ratio once the bytes arrive; the width/height attributes stay the
 * integer server size, which is what holdImage reads as a known box.
 */
export function TranscriptThumbnail(t0) {
  const $ = _c(22);
  let className;
  let maxHeight;
  let props;
  let style;
  let t1;
  if ($[0] !== t0) {
    ({
      maxHeight,
      maxWidth: t1,
      className,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = maxHeight;
    $[3] = props;
    $[4] = style;
    $[5] = t1;
  } else {
    className = $[1];
    maxHeight = $[2];
    props = $[3];
    style = $[4];
    t1 = $[5];
  }
  const maxWidth = t1 === undefined ? Infinity : t1;
  const size = useKnownImageSize(props.src);
  if (!size) {
    let t2;
    if ($[6] !== className || $[7] !== props || $[8] !== style) {
      t2 = <TranscriptImage {...props} className={className} style={style} />;
      $[6] = className;
      $[7] = props;
      $[8] = style;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  const width = Math.min(size.width, size.width * maxHeight / size.height, maxWidth);
  const t2 = size.width;
  const t3 = size.height;
  let t4;
  if ($[10] !== className) {
    t4 = cn(className, "h-auto");
    $[10] = className;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const t5 = `${size.width} / ${size.height}`;
  let t6;
  if ($[12] !== style || $[13] !== t5 || $[14] !== width) {
    t6 = {
      ...style,
      width,
      aspectRatio: t5
    };
    $[12] = style;
    $[13] = t5;
    $[14] = width;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== props || $[17] !== size.height || $[18] !== size.width || $[19] !== t4 || $[20] !== t6) {
    t7 = <TranscriptImage {...props} width={t2} height={t3} className={t4} style={t6} />;
    $[16] = props;
    $[17] = size.height;
    $[18] = size.width;
    $[19] = t4;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
}

/** `loading` for `next/dynamic` transcript chunks: holds the gate until the chunk renders. */
export function TranscriptChunkFallback() {
  const $ = _c(3);
  const gate = useContext(TranscriptLoadGateContext);
  let t0;
  let t1;
  if ($[0] !== gate) {
    t0 = () => gate?.hold("chunk");
    t1 = [gate];
    $[0] = gate;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
  return null;
}